// ═══ ¿QUIÉN DEBÍA CHECAR HOY Y NO LO HA HECHO? ══════════════════════════════
//
// ESTE ARCHIVO EXISTE PARA QUE ESA PREGUNTA TENGA UNA SOLA RESPUESTA.
//
// La lógica nació en `src/App.tsx` y allí sigue, palabra por palabra. Ahora la
// necesita también el servidor —`api/avisos-checador.js`, que manda el push— y
// ese es exactamente el peor sitio donde puede haber dos versiones: una correría
// en el navegador del patrón y la otra en una función de Vercel, y nadie las
// vería discrepar. El patrón leería "faltan 3" en su pantalla y recibiría
// "faltan 5" en el teléfono, sin forma de saber cuál mentía.
//
// Las funciones de aquí son COPIA EXACTA de las de App.tsx, sin tipos. Mientras
// las dos existan, `prueba-checador-compartido` las ejecuta LAS DOS contra el
// mismo juego de casos y falla si dan resultados distintos. Ese es el guardián
// provisional; la casa definitiva es que App.tsx importe de aquí y se borre su
// copia, y eso está a un cambio de distancia (ver el informe de la rama).
//
// NO HAY NADA DE REACT NI DE FIREBASE EN ESTE ARCHIVO. Es aritmética sobre
// objetos planos: por eso se puede compartir. Lo único que toca de Firestore es
// `.toDate?.()`, que existe igual en el SDK del navegador y en firebase-admin.

// ── El horario que rige para una persona ────────────────────────────────────
// UNA SOLA FUNCIÓN PARA TODOS LOS SITIOS: la pantalla del trabajador, la ficha,
// el resumen, la bitácora, el registro mensual, AXIOR y el aviso del servidor.
// App.tsx la IMPORTA de aquí (ya no tiene copia), así que no pueden discrepar.
//
//   1) Su horario PROPIO, si está activo (`horarioPropio.activo === true`) y
//      COMPLETO: trae una hora de entrada válida (HH:MM). Uno activo sin hora de
//      entrada no sirve para medir a nadie, así que rige el general.
//   2) Si no, el GENERAL de la empresa.
//   3) Si tampoco hay, null: no hay horario y no se afirma nada.
//
// VALORES POR OMISIÓN, los mismos en todas partes (los trae ya puestos):
//   · toleranciaMin: 0 si no existe o no es un número; nunca negativa.
//   · umbralFaltaMin: 30 si no existe o no es un número; un 0 se respeta.
//   · diasLaborables: los del horario elegido; si el propio no trae, los del
//     general; si tampoco, null (indeterminable).
// Devuelve una COPIA con esos valores y `origen` ("propio" | "general").
function _horaValida(h) {
  return typeof h === "string" && /^([01]?\d|2[0-3]):[0-5]\d$/.test(h.trim());
}
function toleranciaDe(h) {
  const n = Number(h && h.toleranciaMin);
  return Number.isFinite(n) && n > 0 ? n : 0;
}
function umbralDe(h) {
  const v = h && h.umbralFaltaMin;
  if (v === undefined || v === null || v === "") return 30;
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, n) : 30;
}
function horarioVigente(empleado, horarioGeneral) {
  const propio = empleado && empleado.horarioPropio;
  const usarPropio = !!(propio && propio.activo === true && _horaValida(propio.entrada));
  const base = usarPropio ? propio : (horarioGeneral || null);
  if (!base) return null;
  const vig = diasLaborablesVigentes(base, usarPropio ? horarioGeneral : null);
  return Object.assign({}, base, {
    origen: usarPropio ? "propio" : "general",
    toleranciaMin: toleranciaDe(base),
    umbralFaltaMin: umbralDe(base),
    diasLaborables: vig.dias,
    origenDias: vig.origen,
  });
}

// ── Los días laborables, con su precedencia ─────────────────────────────────
function diasLaborablesDe(horario) {
  const d = horario && horario.diasLaborables;
  if (!Array.isArray(d) || d.length === 0) return null;
  const limpios = d.map((x) => Number(x)).filter((x) => Number.isInteger(x) && x >= 0 && x <= 6);
  return limpios.length ? limpios : null;
}
// El horario personalizado suele serlo por la HORA —alguien entra a las 7 en vez
// de a las 9—, no por los días. Si no trae días, se heredan del general.
//   1) los del horario efectivo de la persona, si los trae;
//   2) si no, los del horario general de la empresa;
//   3) si tampoco, no hay dato y no se afirma nada.
function diasLaborablesVigentes(horarioResuelto, horarioGeneral) {
  const propios = diasLaborablesDe(horarioResuelto);
  if (propios) return { dias: propios, origen: "propio" };
  const generales = diasLaborablesDe(horarioGeneral);
  if (generales) return { dias: generales, origen: "general" };
  return { dias: null, origen: "ninguno" };
}

// ── LA PREGUNTA ─────────────────────────────────────────────────────────────
// LAS CUATRO CONDICIONES SE COMPRUEBAN AQUÍ DENTRO, no repartidas. Si falla
// cualquiera, no se marca. Y la ausencia de dato NO es una falta: sin horario que
// resolver no se marca nada, igual que no se marca a las siete de la mañana ni en
// el día de descanso de esa persona.
//
// `ahora` es un Date cuyos getters LOCALES (getDay, getHours, getMinutes) tienen
// que estar ya en la hora del patrón. En el navegador eso es cierto sin hacer
// nada —el navegador está en México—. En el servidor NO: Vercel corre en UTC, y
// quien llame desde ahí tiene que pasar un Date ya convertido. Ver
// `ahoraEnZona` en el endpoint.
// ═══ ¿DEBE CHECAR EN ESTA FECHA? (13-oct-2026) ═══════════════════════════════
// UNA SOLA FUNCIÓN para todas las preguntas sobre la entrada de una persona en un
// momento dado: "no ha checado" (después de entrada más tolerancia) y el
// recordatorio (los 5 minutos antes de la entrada). Las ventanas van aparte, en
// `debiaChecarHoy` y `tocaRecordatorioEntrada`; las razones para NO exigir la
// entrada, todas aquí y en este orden:
//   sin_cuenta          sin correo, o dada de baja en la ficha;
//   fuera_de_periodo    antes de su ingreso o después de su baja (relacionLaboralEnDia);
//   sin_cuenta          su cuenta no existe o está suspendida (correosConCuenta);
//   sin_horario         no hay horario que la rija, o sin hora de entrada o sin días;
//   no_laborable        ese día no es laborable según el horario que REGÍA en ese
//                       instante (historial h0…hN: el mismo criterio que los documentos);
//   vacaciones          día de vacaciones registrado por el patrón (diasDeVacaciones);
//   descanso_obligatorio día de descanso obligatorio del art. 74 LFT;
//   ya_checo            ya hay una checada de entrada suya ese día.
// Mañana, un motivo nuevo (incapacidades, permisos…) entra aquí y lo ven las dos.
//
//   o.empleado           la ficha (id, email, status, fechas, horarioPropio e historial)
//   o.horarioGeneral     configuracion_empresa.horarioLaboral
//   o.historialGeneral   configuracion_empresa.historialHorario (opcional)
//   o.correosConCuenta   Set de correos con cuenta activa (correosConCuentaActiva)
//   o.checadasHoy        Set de correos que ya checaron entrada ese día (correosQueChecaronHoy)
//   o.registrosVacaciones los registros de cumplimiento_obligaciones (opcional)
//   o.instante           el instante REAL que se pregunta (Date). Si no viene, `o.ahora`:
//                        un Date cuyos getters locales ya dicen la hora de México
//                        (la forma de antes), y si tampoco, ahora.
// Devuelve { debe, motivo?, horario?, iso, minutos }: `iso` es el día en México y
// `minutos`, la hora de México en minutos desde la medianoche.
function _instanteDe(o) {
  if (o && o.instante && typeof o.instante.getTime === "function" && !isNaN(o.instante.getTime())) return o.instante;
  const a = o && o.ahora;
  if (a && typeof a.getTime === "function" && !isNaN(a.getTime()))
    return instanteDeMexico(a.getFullYear(), a.getMonth(), a.getDate(), a.getHours(), a.getMinutes(), a.getSeconds(), a.getMilliseconds());
  return new Date();
}
function debeChecarEnFecha(o) {
  const e = o.empleado;
  const correo = String((e && e.email) || "").toLowerCase().trim();
  const instante = _instanteDe(o);
  const mx = enHoraDeMexico(instante);
  const iso = isoDiaLocal(mx);
  const minutos = mx.getHours() * 60 + mx.getMinutes();
  const sale = (motivo, extra) => Object.assign({ debe: false, motivo, iso, minutos }, extra || {});

  if (!correo || ((e && e.status) || "activo") === "baja") return sale("sin_cuenta");
  if (relacionLaboralEnDia(periodosLaboralesDe(e), iso) !== "dentro") return sale("fuera_de_periodo");
  if (!o.correosConCuenta || !o.correosConCuenta.has(correo)) return sale("sin_cuenta");

  // El horario que REGÍA en ese instante; sin historial es el de ahora (horarioVigente).
  const h = horarioQueRegia(e, o.horarioGeneral, o.historialGeneral || null, instante);
  if (!h || !_horaValida(h.entrada)) return sale("sin_horario");
  const dias = diasLaborablesDe(h);
  if (!dias || !dias.length) return sale("sin_horario");
  if (dias.indexOf(mx.getDay()) < 0) return sale("no_laborable", { horario: h });

  // Vacaciones y art. 74, con el calendario del año (y del anterior: un periodo de
  // vacaciones de diciembre puede seguir en enero).
  const anio = mx.getFullYear();
  const descansos = Object.assign({}, descansosObligatoriosDe(anio - 1) || {}, descansosObligatoriosDe(anio) || {});
  if (o.registrosVacaciones && e && e.id) {
    const vac = diasDeVacaciones(o.registrosVacaciones, e.id, dias, descansos);
    if (vac[iso]) return sale("vacaciones", { horario: h });
  }
  if (descansos[iso]) return sale("descanso_obligatorio", { horario: h, descanso: descansos[iso] });

  if (o.checadasHoy && o.checadasHoy.has(correo)) return sale("ya_checo", { horario: h });
  return { debe: true, horario: h, iso, minutos };
}
// La hora de entrada de un HORARIO, en minutos desde la medianoche. NaN si no hay.
// (Otro nombre que `_minutosDeEntrada`, más abajo, que recibe el texto "HH:MM".)
function _minutosDeEntradaDe(h) {
  const partes = String((h && h.entrada) || "").split(":");
  const hh = parseInt(partes[0], 10), mm = parseInt(partes[1], 10);
  return isNaN(hh) || isNaN(mm) ? NaN : hh * 60 + mm;
}

// ── "NO HA CHECADO": después de la entrada más la tolerancia ────────────────
// Igual que siempre, salvo lo que pide la base nueva (horario del instante,
// vacaciones y art. 74). EL ORDEN DE LOS MOTIVOS SE CONSERVA: antes de la hora,
// quien ya checó sigue diciendo "aun_no_toca", como antes —el resumen del equipo
// cuenta con eso para decir "todavía no es la hora de entrada de nadie"—.
function debiaChecarHoy(o) {
  const b = debeChecarEnFecha(o);
  if (!b.debe && b.motivo !== "ya_checo") return { marca: false, motivo: b.motivo };
  const entrada = _minutosDeEntradaDe(b.horario);
  if (isNaN(entrada)) return { marca: false, motivo: "sin_horario" };
  const tol = Math.max(0, Number(b.horario.toleranciaMin) || 0);
  if (b.minutos < entrada + tol) return { marca: false, motivo: "aun_no_toca" };
  if (b.motivo === "ya_checo") return { marca: false, motivo: "ya_checo" };
  return { marca: true };
}

// ── EL RECORDATORIO: en los `minutosAntes` (5) antes de la entrada ──────────
// La misma base; ya checado no se recuerda. Devuelve { toca, motivo?, entrada? }:
// `entrada` es la hora "HH:MM" del horario que rige, para el texto del aviso.
function tocaRecordatorioEntrada(o, minutosAntes) {
  const antes = Number.isFinite(Number(minutosAntes)) && Number(minutosAntes) > 0 ? Number(minutosAntes) : 5;
  const b = debeChecarEnFecha(o);
  if (!b.debe) return { toca: false, motivo: b.motivo };
  const entrada = _minutosDeEntradaDe(b.horario);
  if (isNaN(entrada)) return { toca: false, motivo: "sin_horario" };
  if (b.minutos < entrada - antes) return { toca: false, motivo: "aun_no_toca" };
  if (b.minutos >= entrada) return { toca: false, motivo: "ya_paso" };
  return { toca: true, entrada: String(b.horario.entrada).trim() };
}

// ── EL DÍA DEL CALENDARIO de un Date, con sus getters locales ("AAAA-MM-DD") ─
function isoDiaLocal(d) {
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}

// ═══ DÍAS DE DESCANSO OBLIGATORIO, ART. 74 LFT (13-oct-2026) ═════════════════
// Texto vigente (última reforma DOF 14-05-2026), CALCULADO, no tecleado por año:
//   I    1 de enero                            "Año Nuevo"
//   II   primer lunes de febrero               "Constitución"
//   III  tercer lunes de marzo                 "Natalicio de Juárez"
//   IV   1 de mayo                             "1° de Mayo"
//   V    16 de septiembre                      "Independencia"
//   VI   tercer lunes de noviembre             "Revolución"
//   VII  1 de octubre de cada seis años, cuando corresponde a la transmisión del
//        Poder Ejecutivo Federal (reforma DOF 30-09-2024; antes era el 1 de
//        diciembre): 2024, 2030, 2036…        "Transmisión del Poder Ejecutivo Federal"
//   VIII 25 de diciembre                       "Navidad"
//   IX   EL DÍA DE LA JORNADA ELECTORAL: NO SE CALCULA. Lo fijan las leyes
//        electorales en cada proceso; queda fuera de esta lista a propósito.
// Los nombres son los que los documentos ya imprimían. Para años anteriores a 2024
// (cuando la fracción VII era el 1 de diciembre) devuelve null: "no se sabe", que
// quien llame dice con palabras, igual que antes con los años sin catálogo.
function _nLunes(anio, mes0, n) {
  const d1 = new Date(anio, mes0, 1);
  const primerLunes = 1 + ((8 - d1.getDay()) % 7);
  return new Date(anio, mes0, primerLunes + 7 * (n - 1));
}
function descansosObligatoriosDe(anio) {
  const a = Number(anio);
  if (!Number.isInteger(a) || a < 2024) return null;
  const m = {};
  m[isoDiaLocal(new Date(a, 0, 1))] = "Año Nuevo";
  m[isoDiaLocal(_nLunes(a, 1, 1))] = "Constitución";
  m[isoDiaLocal(_nLunes(a, 2, 3))] = "Natalicio de Juárez";
  m[isoDiaLocal(new Date(a, 4, 1))] = "1° de Mayo";
  m[isoDiaLocal(new Date(a, 8, 16))] = "Independencia";
  if ((a - 2024) % 6 === 0) m[isoDiaLocal(new Date(a, 9, 1))] = "Transmisión del Poder Ejecutivo Federal";
  m[isoDiaLocal(_nLunes(a, 10, 3))] = "Revolución";
  m[isoDiaLocal(new Date(a, 11, 25))] = "Navidad";
  return m;
}

// ═══ DÍAS CUBIERTOS POR VACACIONES (movida de App.tsx, 13-oct-2026) ═══════════
// El registro (cumplimiento_obligaciones, tipo "vacaciones") guarda fecha de inicio
// y número de días, sin fecha de fin, así que hay que extenderlo. Se cuentan DÍAS
// LABORABLES, no naturales (art. 76 LFT), y los del art. 74 no consumen
// vacaciones. Sin días laborables no se puede extender: devuelve vacío.
function diasDeVacaciones(registros, trabajadorId, laborables, descansos) {
  const mapa = {};
  if (!laborables || !trabajadorId) return mapa;
  (registros || []).forEach((r) => {
    if (!r || r.tipo !== "vacaciones" || r.trabajadorId !== trabajadorId) return;
    const p = String(r.fecha || "").slice(0, 10).split("-");
    if (p.length !== 3) return;
    const ini = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
    if (isNaN(ini.getTime())) return;
    let quedan = Math.round(Number(r.diasTomados) || 0);
    if (quedan <= 0) return;
    const cursor = new Date(ini);
    let vueltas = 0;
    while (quedan > 0 && vueltas++ < 400) {       // tope: un periodo no dura años
      const iso = isoDiaLocal(cursor);
      const esLaborable = laborables.indexOf(cursor.getDay()) >= 0;
      const es74 = !!(descansos && descansos[iso]);
      if (esLaborable && !es74) { mapa[iso] = true; quedan--; }
      cursor.setDate(cursor.getDate() + 1);
    }
  });
  return mapa;
}

// ── Los dos conjuntos que la función necesita ───────────────────────────────
// `correosConCuenta` se DERIVA de la colección cuentas_trabajador, no de la
// bandera `tieneAcceso` de la ficha: esa bandera es una copia que solo escriben
// las rutas que crean la cuenta desde la app.
function correosConCuentaActiva(cuentasTrab) {
  return new Set((cuentasTrab || [])
    .filter((c) => c && c.activo !== false)
    .map((c) => String((c && (c.email || c.id)) || "").toLowerCase().trim())
    .filter(Boolean));
}

// `aLocal` ES LA ÚNICA COSTURA QUE ESTE ARCHIVO AÑADE A LA VERSIÓN DE App.tsx, y
// por omisión no cambia nada: es la identidad.
//
// El motivo: aquí se compara el DÍA DEL CALENDARIO de cada checada contra el día
// de `ahora`, y `Date.getFullYear/Month/Date` responden en la zona de quien
// ejecuta. En el navegador del patrón eso es México y sale bien. En Vercel es
// UTC, y una checada de las 19:00 de México cae en el día siguiente para el
// servidor. Por eso el endpoint pasa aquí su conversor a hora de México, y el
// navegador no pasa nada y se comporta exactamente como antes.
function correosQueChecaronHoy(checadas, ahora, aLocal) {
  const conv = aLocal || ((d) => d);
  const d0 = conv(ahora || new Date());
  const mismoDia = (d) => d.getFullYear() === d0.getFullYear() && d.getMonth() === d0.getMonth() && d.getDate() === d0.getDate();
  const horaDe = (c) => (c && c.horaServidor && c.horaServidor.toDate && c.horaServidor.toDate())
                     || (c && c.timestamp && c.timestamp.toDate && c.timestamp.toDate())
                     || null;
  return new Set((checadas || [])
    .filter((c) => { const d = horaDe(c); return c && c.tipo === "entrada" && d && mismoDia(conv(d)); })
    .map((c) => String((c && c.trabajadorEmail) || "").toLowerCase().trim())
    .filter(Boolean));
}

// ═══ CHECADAS INALTERABLES (FASE 2a): EL HORARIO QUE REGÍA ════════════════════
//
// UNA CHECADA SE CLASIFICA CON EL HORARIO QUE REGÍA CUANDO SE HIZO, no con el de
// hoy. Antes, cambiar el horario un martes reclasificaba todo el mes hacia atrás:
// un "a tiempo" del lunes podía volverse "retardo" en una bitácora ya firmada.
// Ahora cada guardado del horario deja una entrada en su HISTORIAL (h0, h1, h2…,
// ver firestore.rules), y de aquí sale qué horario regía en cada instante. Las
// pantallas, los documentos y AXIOR lo preguntan todos aquí.

// ── La hora de México, la mire quien la mire ────────────────────────────────
// Se clasifica con la hora del SERVIDOR (el `timestamp` de la checada) leída en
// America/Mexico_City, no en la zona de quien abre la pantalla: un patrón de
// viaje vería otra hora y otra clasificación. `enHoraDeMexico` devuelve un Date
// "local ficticio" cuyos getters LOCALES (getHours, getDate, getDay…) dan la hora
// de México. Sirve SOLO para leer esos getters: para comparar instantes (contra
// el historial, por ejemplo) se usa el Date real.
const ZONA_MEXICO = "America/Mexico_City";
let _fmtMexico = null;
function enHoraDeMexico(instante) {
  if (!instante || typeof instante.getTime !== "function" || isNaN(instante.getTime())) return null;
  if (!_fmtMexico) {
    _fmtMexico = new Intl.DateTimeFormat("en-CA", {
      timeZone: ZONA_MEXICO, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    });
  }
  const p = {};
  _fmtMexico.formatToParts(instante).forEach((x) => { p[x.type] = x.value; });
  // La hora 24 existe en algunas configuraciones de `hour12:false`; se normaliza.
  return new Date(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour) % 24,
    Number(p.minute), Number(p.second), instante.getMilliseconds());
}

// Y AL REVÉS: el instante real en que en México son `h:mi` del día `anio/mes0/dia`.
// Se calcula el desfase con Intl en vez de suponer UTC−6: es una ley, no una
// constante, y ya cambió una vez.
function instanteDeMexico(anio, mes0, dia, h, mi, s, ms) {
  const supuesto = Date.UTC(anio, mes0, dia, h || 0, mi || 0, s || 0, ms || 0);
  const mx = enHoraDeMexico(new Date(supuesto));
  const comoUTC = Date.UTC(mx.getFullYear(), mx.getMonth(), mx.getDate(), mx.getHours(), mx.getMinutes(), mx.getSeconds(), mx.getMilliseconds());
  return new Date(supuesto - (comoUTC - supuesto));
}

// ── El historial: un mapa h0, h1, h2… que solo crece ────────────────────────
// Milisegundos de una marca de Firestore (del navegador o de firebase-admin), de
// un Date o de un {seconds, nanoseconds}. NaN si no hay marca.
function _msDeMarca(v) {
  if (!v) return NaN;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.getTime === "function") return v.getTime();
  if (typeof v.seconds === "number") return v.seconds * 1000 + Math.floor((Number(v.nanoseconds) || 0) / 1e6);
  return NaN;
}
// Las entradas, en su orden (h0, h1, h2…). Lo que no tenga forma de entrada se ignora.
function entradasDelHistorial(historial) {
  if (!historial || typeof historial !== "object") return [];
  return Object.keys(historial)
    .filter((k) => /^h\d+$/.test(k) && historial[k] && typeof historial[k] === "object")
    .map((k) => ({ n: Number(k.slice(1)), e: historial[k] }))
    .sort((a, b) => a.n - b.n)
    .map((x) => x.e);
}
function historialVacio(historial) {
  return entradasDelHistorial(historial).length === 0;
}
// LA CLAVE DE LA SIGUIENTE ENTRADA SE CUENTA COMO LA CUENTAN LAS REGLAS:
// `'h' + string(antes.size())`, con TODAS las claves del mapa. Contarla de otra
// forma haría que el guardado bueno saliera rechazado.
function siguienteClaveDeHistorial(historial) {
  const n = historial && typeof historial === "object" ? Object.keys(historial).length : 0;
  return "h" + n;
}
// Lo que se guarda de un horario en su historial: los campos que admiten las
// reglas (`horarioGuardadoValido`), sin `actualizadoEn` —esa fecha la lleva la
// entrada, como `vigenteDesde`, y es la del servidor—.
const CAMPOS_HORARIO_HISTORIAL = ["entrada", "salida", "usaComida", "comidaInicio", "comidaFin",
  "toleranciaMin", "umbralFaltaMin", "diasLaborables", "activo"];
function horarioParaHistorial(h) {
  if (!h || typeof h !== "object") return null;
  const r = {};
  CAMPOS_HORARIO_HISTORIAL.forEach((k) => { if (h[k] !== undefined) r[k] = h[k]; });
  return r;
}
// Una entrada. `marcaDelServidor` es el serverTimestamp() de quien escribe: las
// reglas exigen que `vigenteDesde` sea la hora del servidor y que `por` sea el
// correo de la sesión. `desdeSiempre` solo en h0: el horario que ya existía.
function entradaDeHistorial(horario, correo, marcaDelServidor, desdeSiempre) {
  const e = { vigenteDesde: marcaDelServidor, horario: horarioParaHistorial(horario), por: correo };
  if (desdeSiempre) e.desdeSiempre = true;
  return e;
}

// EL HORARIO QUE REGÍA EN `instante`, de un historial y del valor de ahora:
//   · sin historial: el de ahora. Así están las empresas hasta su primer guardado
//     con esta versión, y así se clasificaba todo antes;
//   · antes de la primera entrada: la primera (h0 es "desde siempre");
//   · entre dos entradas: la que empezó antes;
//   · desde la última entrada: el de ahora. Suele ser el mismo que el de la última
//     entrada; si no lo es, alguien lo cambió con una versión anterior de la app,
//     que no escribía historial, y lo que rige es lo que está guardado;
//   · una entrada todavía sin fecha (escrita hace un instante en este navegador,
//     que aún no recibe la del servidor) cuenta como de ahora.
// POR INSTANTE, NO POR DÍA: si el horario cambia a las 10:00, la entrada de las
// 09:05 de ese mismo día se califica con el de antes. Es lo que el teléfono tenía
// al checar, y lo que escribe como testigo.
function horarioDelHistorial(historial, actual, instante) {
  const actualONulo = actual == null ? null : actual;
  const es = entradasDelHistorial(historial);
  const t = instante && typeof instante.getTime === "function" ? instante.getTime() : NaN;
  if (!es.length || !Number.isFinite(t)) return actualONulo;
  const ahora = Date.now();
  let i = -1;
  for (let k = 0; k < es.length; k++) {
    const ms = _msDeMarca(es[k].vigenteDesde);
    if ((Number.isFinite(ms) ? ms : ahora) <= t) i = k;
  }
  if (i === es.length - 1) return actualONulo;
  const e = es[i < 0 ? 0 : i];
  return e.horario == null ? null : e.horario;
}
// DESDE CUÁNDO HAY HISTORIAL: la `vigenteDesde` más antigua de los historiales
// dados (los que existan). Antes de ese instante se aplica la primera entrada —h0,
// "desde siempre"—, que es el horario más antiguo registrado. null si ninguno
// tiene entradas con fecha.
function primeraVigenciaDelHistorial() {
  let min = Infinity;
  for (let i = 0; i < arguments.length; i++) {
    const es = entradasDelHistorial(arguments[i]);
    if (!es.length) continue;
    const ms = _msDeMarca(es[0].vigenteDesde);
    if (Number.isFinite(ms) && ms < min) min = ms;
  }
  return Number.isFinite(min) ? new Date(min) : null;
}
// EL HORARIO QUE REGÍA PARA UNA PERSONA EN UN INSTANTE: su propio y el general,
// cada uno de su historial, y después la MISMA precedencia de siempre
// (horarioVigente). Sin `instante`, es exactamente horarioVigente: el de ahora.
//   empleado        la ficha (trae horarioPropio e historialHorarioPropio)
//   horarioGeneral  configuracion_empresa.horarioLaboral
//   historialGeneral configuracion_empresa.historialHorario
//
// CON `instante`, EL HORARIO DEVUELTO DICE TAMBIÉN DESDE CUÁNDO RIGE (para que el
// papel lo imprima tal como se aplicó, sin leer el historial por su cuenta):
//   · `origen`: "propio" (personalizado) o "general";
//   · `vigenteDesde`: la fecha de la entrada del historial que se aplicó, o null;
//   · `vigenciaConsta`: false si el instante es ANTERIOR a toda entrada del
//     historial de donde salió (se aplicó la primera, "desde siempre"), o si ese
//     horario no tiene historial. Si la ficha tiene historial propio y el instante
//     es anterior a él, tampoco consta si entonces regía o no un propio.
function horarioQueRegia(empleado, horarioGeneral, historialGeneral, instante) {
  const general = horarioDelHistorial(historialGeneral, horarioGeneral || null, instante);
  const propio = empleado ? horarioDelHistorial(empleado.historialHorarioPropio, empleado.horarioPropio, instante) : null;
  const h = empleado ? horarioVigente(Object.assign({}, empleado, { horarioPropio: propio }), general) : horarioVigente(null, general);
  if (!h || !instante || typeof instante.getTime !== "function" || isNaN(instante.getTime())) return h;
  const vG = vigenciaEnElHistorial(historialGeneral, instante);
  const vP = empleado && entradasDelHistorial(empleado.historialHorarioPropio).length ? vigenciaEnElHistorial(empleado.historialHorarioPropio, instante) : null;
  const v = h.origen === "propio" ? (vP || { desde: null, consta: false }) : vG;
  // EL ÚLTIMO CAMBIO que pudo cambiar lo que se aplica: la entrada más reciente de
  // cualquiera de los dos historiales (encender o apagar el propio también cuenta).
  const cambios = [vG.desde, vP && vP.desde].filter((d) => d && !isNaN(d.getTime()));
  const ultimoCambioEn = cambios.length ? new Date(Math.max.apply(null, cambios.map((d) => d.getTime()))) : null;
  return Object.assign(h, { vigenteDesde: v.desde, vigenciaConsta: v.consta && (!vP || vP.consta), ultimoCambioEn });
}
// DESDE CUÁNDO RIGE, en un historial, lo que se aplica en `instante`: la fecha de
// la entrada elegida (consta), o nada si el instante es anterior a la primera
// entrada o no hay historial (no consta).
function vigenciaEnElHistorial(historial, instante) {
  const es = entradasDelHistorial(historial);
  const t = instante && typeof instante.getTime === "function" ? instante.getTime() : NaN;
  if (!es.length || !Number.isFinite(t)) return { desde: null, consta: false };
  const ahora = Date.now();
  const msDe = (e) => { const m = _msDeMarca(e.vigenteDesde); return Number.isFinite(m) ? m : ahora; };
  let i = -1;
  for (let k = 0; k < es.length; k++) if (msDe(es[k]) <= t) i = k;
  if (i < 0) return { desde: null, consta: false };
  // UNA ENTRADA CON EL MISMO HORARIO QUE LA ANTERIOR NO ES UN CAMBIO: la pantalla
  // del horario general la escribe también cuando solo se movió la ubicación o el
  // radio. El horario rige desde la primera de esas entradas iguales seguidas.
  while (i > 0 && _firmaDeHistorial(es[i - 1].horario) === _firmaDeHistorial(es[i].horario)) i--;
  return { desde: new Date(msDe(es[i])), consta: true };
}
// Lo que dice un horario del historial, para comparar dos: sus campos (los de
// horarioParaHistorial), en orden, y los días ordenados.
function _firmaDeHistorial(h) {
  const p = horarioParaHistorial(h);
  if (!p) return "null";
  if (Array.isArray(p.diasLaborables)) p.diasLaborables = p.diasLaborables.map(Number).sort((a, b) => a - b);
  return JSON.stringify(Object.keys(p).sort().map((k) => [k, p[k]]));
}

// ── La relación laboral: antes del ingreso y después de la baja no se cuenta ─
// LOS PERIODOS DE LA RELACIÓN LABORAL de una ficha (10-oct-2026): los de
// `periodosLaborales` si los tiene ({ingreso, baja?}, uno por contratación); si no,
// uno solo desde `fechaIngreso` (la PRIMERA contratación) hasta `fechaBaja` (la
// última salida), que es todo lo que guardaban las fichas viejas. Lo mismo que
// `periodosDe` de App.tsx. Sin fecha de ingreso, [] (y todo se cuenta como siempre).
function periodosLaboralesDe(t) {
  const guardados = t && Array.isArray(t.periodosLaborales) ? t.periodosLaborales : [];
  if (guardados.length) return guardados;
  if (!t || !t.fechaIngreso) return [];
  const uno = { ingreso: t.fechaIngreso };
  if (t.fechaBaja) uno.baja = t.fechaBaja;
  if (t.razonBaja) uno.razonBaja = t.razonBaja;
  return [uno];
}
const _FECHA = /^\d{4}-\d{2}-\d{2}/;
// EN QUÉ PARTE DE SU RELACIÓN LABORAL CAE UN DÍA ("AAAA-MM-DD"):
//   · "dentro"        entre un ingreso y su baja (ambos días incluidos), o sin baja;
//   · "antes_ingreso" antes de su primer ingreso;
//   · "despues_baja"  después de una baja y antes del siguiente ingreso, o después
//                     de la última baja.
// Sin periodos con fecha válida: "dentro" (todo queda como siempre).
function relacionLaboralEnDia(periodos, iso) {
  const ps = (periodos || [])
    .filter((p) => p && _FECHA.test(String(p.ingreso || "")))
    .map((p) => ({ ini: String(p.ingreso).slice(0, 10), fin: _FECHA.test(String(p.baja || "")) ? String(p.baja).slice(0, 10) : null }))
    .sort((a, b) => (a.ini < b.ini ? -1 : a.ini > b.ini ? 1 : 0));
  const d = String(iso || "").slice(0, 10);
  if (!ps.length || !_FECHA.test(d)) return "dentro";
  if (d < ps[0].ini) return "antes_ingreso";
  for (const p of ps) if (d >= p.ini && (!p.fin || d <= p.fin)) return "dentro";
  return "despues_baja";
}
// ¿HOY ESTÁ DENTRO DE SU RELACIÓN LABORAL? (11-oct-2026) La pregunta de
// `debiaChecarHoy`, con las MISMAS dos funciones de arriba (no una copia): ingreso
// futuro, baja ya pasada (aunque el estatus diga "activo") o entre una baja y un
// reingreso, no debía checar. `ahora` con sus getters locales en la hora del
// patrón, como en `debiaChecarHoy`. Sin fecha de ingreso, "dentro".
function relacionLaboralHoy(empleado, ahora) {
  const d = ahora || new Date();
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return relacionLaboralEnDia(periodosLaboralesDe(empleado), iso);
}
// La fecha que explica por qué un día no cuenta: la del primer ingreso, o la de la
// última baja anterior a ese día. "" si no aplica.
function fechaDeLaRelacion(periodos, iso, relacion) {
  const ps = (periodos || []).filter((p) => p && _FECHA.test(String(p.ingreso || "")));
  const d = String(iso || "").slice(0, 10);
  if (relacion === "antes_ingreso") return ps.map((p) => String(p.ingreso).slice(0, 10)).sort()[0] || "";
  if (relacion === "despues_baja") return ps.map((p) => String(p.baja || "").slice(0, 10)).filter((b) => _FECHA.test(b) && b < d).sort().pop() || "";
  return "";
}

// ── Los cambios de horario de un periodo, del historial ─────────────────────
// LOS INSTANTES EN QUE PUDO CAMBIAR EL HORARIO de una persona entre `desde` y
// `hasta` (incluidos): las fechas de las entradas de su historial propio y del
// general. Si de verdad cambió lo decide quien los usa, comparando el horario que
// regía justo antes y justo en ese instante CON LA MISMA FUNCIÓN QUE CALIFICA.
// Sale del historial, no de los días que se calificaron: un cambio que no llegó a
// calificar ninguna entrada también consta.
function instantesDelHistorial(historialPropio, historialGeneral, desde, hasta) {
  const a = desde && typeof desde.getTime === "function" ? desde.getTime() : -Infinity;
  const b = hasta && typeof hasta.getTime === "function" ? hasta.getTime() : Infinity;
  const ms = [];
  [historialPropio, historialGeneral].forEach((h) => entradasDelHistorial(h).forEach((e) => {
    const m = _msDeMarca(e.vigenteDesde);
    if (Number.isFinite(m) && m >= a && m <= b && ms.indexOf(m) < 0) ms.push(m);
  }));
  return ms.sort((x, y) => x - y).map((m) => new Date(m));
}
// EL HORARIO QUE SE APLICA, PARA COMPARAR DOS (el que devuelven horarioVigente y
// horarioQueRegia): de dónde sale —propio o general— y lo que dice, con los días
// ordenados. Dos iguales son el mismo horario aunque vengan de entradas distintas.
function firmaDelHorarioAplicado(h) {
  if (!h || typeof h !== "object") return "null";
  const p = horarioParaHistorial(h) || {};
  delete p.activo;
  if (Array.isArray(p.diasLaborables)) p.diasLaborables = p.diasLaborables.map(Number).sort((x, y) => x - y);
  return JSON.stringify([h.origen === "propio" ? "propio" : "general", Object.keys(p).sort().map((k) => [k, p[k]])]);
}

// ── Volver a encender el personalizado ──────────────────────────────────────
// EL ÚLTIMO HORARIO PERSONALIZADO CON VALORES: la entrada más reciente del
// historial propio que trae un personalizado encendido con hora de entrada, y
// hasta cuándo rigió (la fecha de la entrada que le siguió). null si no hay
// ninguno. Sirve para PRELLENAR el formulario; no se guarda nada con él.
function ultimoPersonalizadoDelHistorial(historial) {
  const es = entradasDelHistorial(historial);
  for (let k = es.length - 1; k >= 0; k--) {
    const h = es[k].horario;
    if (h && h.activo === true && _horaValida(h.entrada)) {
      const sig = es[k + 1] ? _msDeMarca(es[k + 1].vigenteDesde) : NaN;
      return { horario: Object.assign({}, h), vigenteHasta: Number.isFinite(sig) ? new Date(sig) : null };
    }
  }
  return null;
}

// ── El testigo: el horario que tenía el teléfono al checar ──────────────────
// El teléfono escribe en la checada `horarioAplicado`: lo que le resolvió
// horarioVigente en ese momento. NO decide la clasificación —la decide el
// historial—; sirve para ver si el teléfono trabajaba con otro horario.
// La forma es la que exigen las reglas (`horarioAplicadoValido`): si el horario
// no tiene una hora de entrada válida, es null. Así una checada nunca se rechaza
// por su testigo.
const _ENTRADA_VALIDA = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
function horarioAplicadoDe(he) {
  if (!he || typeof he !== "object") return null;
  const entrada = String(he.entrada == null ? "" : he.entrada).trim();
  if (!_ENTRADA_VALIDA.test(entrada)) return null;
  return {
    entrada,
    toleranciaMin: toleranciaDe(he),
    umbralFaltaMin: umbralDe(he),
    diasLaborables: diasLaborablesDe(he),
    origen: he.origen === "propio" ? "propio" : "general",
  };
}
function _minutosDeEntrada(e) {
  const p = String(e || "").trim().split(":");
  return parseInt(p[0], 10) * 60 + parseInt(p[1], 10);
}
function _mismosDias(a, b) {
  const x = Array.isArray(a) && a.length ? a.map(Number).sort() : null;
  const y = Array.isArray(b) && b.length ? b.map(Number).sort() : null;
  if (!x || !y) return !x && !y;
  return x.length === y.length && x.every((v, i) => v === y[i]);
}
// ¿El teléfono registró OTRO horario que el del historial? Se comparan la hora
// de entrada, la tolerancia, el umbral y los días; el `origen` no (propio o
// general con las mismas horas es el mismo horario). Una checada sin testigo
// —todas las de antes de la fase 2a— no difiere de nada.
function difiereDelTestigo(checada, horarioDelHistorialResuelto) {
  if (!checada || !("horarioAplicado" in checada) || checada.horarioAplicado === undefined) return false;
  const t = checada.horarioAplicado;
  const h = horarioAplicadoDe(horarioDelHistorialResuelto);
  if (t === null || h === null) return (t === null) !== (h === null);
  return _minutosDeEntrada(t.entrada) !== _minutosDeEntrada(h.entrada)
    || Number(t.toleranciaMin) !== h.toleranciaMin
    || Number(t.umbralFaltaMin) !== h.umbralFaltaMin
    || !_mismosDias(t.diasLaborables, h.diasLaborables);
}

// ── CommonJS a proposito ────────────────────────────────────────────────────
// NO es ESM, y no es un descuido. La funcion de Vercel se escribe con sintaxis
// ESM en un .js sin "type":"module", asi que Vercel la TRANSPILA a CommonJS
// antes de ejecutarla: su import se convierte en un require(). Un require() de
// un modulo ESM revienta en Node 20 —ERR_REQUIRE_ESM— al CARGAR el archivo,
// antes de que exista el handler: la funcion devuelve 500 sin llegar siquiera a
// comprobar el secreto. Paso exactamente eso.
//
// Se exporta nombre por nombre, no con un module.exports = {...}, porque asi lo
// detecta con certeza el lexer que Node usa cuando un ESM importa un CJS: el
// archivo sirve para los dos mundos sin depender de como decida cargarlo nadie.
exports.horarioVigente = horarioVigente;
exports.toleranciaDe = toleranciaDe;
exports.umbralDe = umbralDe;
exports.diasLaborablesDe = diasLaborablesDe;
exports.diasLaborablesVigentes = diasLaborablesVigentes;
exports.debiaChecarHoy = debiaChecarHoy;
exports.debeChecarEnFecha = debeChecarEnFecha;
exports.tocaRecordatorioEntrada = tocaRecordatorioEntrada;
exports.isoDiaLocal = isoDiaLocal;
exports.descansosObligatoriosDe = descansosObligatoriosDe;
exports.diasDeVacaciones = diasDeVacaciones;
exports.correosConCuentaActiva = correosConCuentaActiva;
exports.correosQueChecaronHoy = correosQueChecaronHoy;
exports.ZONA_MEXICO = ZONA_MEXICO;
exports.enHoraDeMexico = enHoraDeMexico;
exports.instanteDeMexico = instanteDeMexico;
exports.entradasDelHistorial = entradasDelHistorial;
exports.historialVacio = historialVacio;
exports.siguienteClaveDeHistorial = siguienteClaveDeHistorial;
exports.CAMPOS_HORARIO_HISTORIAL = CAMPOS_HORARIO_HISTORIAL;
exports.horarioParaHistorial = horarioParaHistorial;
exports.entradaDeHistorial = entradaDeHistorial;
exports.horarioDelHistorial = horarioDelHistorial;
exports.primeraVigenciaDelHistorial = primeraVigenciaDelHistorial;
exports.horarioQueRegia = horarioQueRegia;
exports.vigenciaEnElHistorial = vigenciaEnElHistorial;
exports.periodosLaboralesDe = periodosLaboralesDe;
exports.relacionLaboralEnDia = relacionLaboralEnDia;
exports.relacionLaboralHoy = relacionLaboralHoy;
exports.fechaDeLaRelacion = fechaDeLaRelacion;
exports.instantesDelHistorial = instantesDelHistorial;
exports.firmaDelHorarioAplicado = firmaDelHorarioAplicado;
exports.ultimoPersonalizadoDelHistorial = ultimoPersonalizadoDelHistorial;
exports.horarioAplicadoDe = horarioAplicadoDe;
exports.difiereDelTestigo = difiereDelTestigo;
